import { ref, type Ref } from 'vue'
import { useAuthStore } from '@/stores'
import { countQueuedCompletionsForUser } from '@/utils/completionQueue'
import { captureError } from '@/utils/sentry'
import { flushPendingActions } from '@/utils/pendingActions'

export interface UseConfirmLogoutReturn {
  showPendingWarning: Ref<boolean>
  pendingCount: Ref<number>
  requestLogout: () => Promise<void>
  confirmLogout: () => Promise<void>
}

export function useConfirmLogout(performLogout: () => Promise<void>): UseConfirmLogoutReturn {
  const authStore = useAuthStore()
  const showPendingWarning = ref(false)
  const pendingCount = ref(0)

  async function flushAndLogout(): Promise<void> {
    await flushPendingActions()
    await performLogout()
  }

  async function requestLogout(): Promise<void> {
    const user = authStore.user
    if (!user) {
      await flushAndLogout()
      return
    }

    pendingCount.value = 0
    try {
      pendingCount.value = await countQueuedCompletionsForUser(user.id)
    } catch (error) {
      captureError(error instanceof Error ? error : new Error('Failed to count queued completions'))
      showPendingWarning.value = true
      return
    }

    if (pendingCount.value === 0) {
      await flushAndLogout()
      return
    }
    showPendingWarning.value = true
  }

  async function confirmLogout(): Promise<void> {
    showPendingWarning.value = false
    await flushAndLogout()
  }

  return {
    showPendingWarning,
    pendingCount,
    requestLogout,
    confirmLogout,
  }
}
